/**
 * Fetches a documentation page from the Docusaurus site (docs.vnetwork.dev) and
 * extracts just its rendered article so the in-app Documentation panel can
 * re-render it natively with @vnetwork/ui tokens (no iframe).
 *
 * The docs site is our own first-party, static, CORS-open SSG — but we still
 * sanitize (strip scripts/handlers/embeds) before injecting the HTML.
 */
import { env } from '@/config/env';

export const DOCS_BASE = env.docsUrl.replace(/\/$/, '');
export const DOCS_HOME = '/docs/intro/';

/**
 * When the docs site is served under a Docusaurus `baseUrl` subpath (e.g.
 * `https://pages.vnetwork.dev/documentation-eca8de/`) instead of at a domain
 * root, its rendered HTML emits every internal URL as site-absolute *including*
 * that subpath (`/documentation-eca8de/assets/…`, `/documentation-eca8de/docs/…`).
 *
 * We keep one canonical internal representation — subpath-free doc paths like
 * `/docs/…` — and add the subpath back in exactly one place (via `DOCS_BASE`).
 * `DOCS_ORIGIN` prefixes site-absolute asset URLs that already carry the subpath;
 * `DOCS_BASE_PATH` is the subpath we strip off HTML-extracted links. Both are
 * empty/root for a domain-root deploy, so this is a no-op there.
 */
export const DOCS_ORIGIN = new URL(DOCS_BASE).origin;
export const DOCS_BASE_PATH = new URL(DOCS_BASE).pathname.replace(/\/+$/, '');

/**
 * The docs site uses `trailingSlash: true` and 301-redirects bare paths to their
 * slashed form. A CORS fetch that follows that 301 fails whenever the browser has
 * a *cached* copy of the pre-CORS 301 (Chrome caches permanent redirects very
 * aggressively, so the replayed hop lacks the Access-Control-Allow-Origin header).
 * We sidestep it entirely by always requesting the canonical trailing-slash URL —
 * no redirect hop, nothing stale to replay.
 */
export function canonicalDocPath(path: string): string {
  const hashIdx = path.indexOf('#');
  const hash = hashIdx >= 0 ? path.slice(hashIdx) : '';
  let p = hashIdx >= 0 ? path.slice(0, hashIdx) : path;
  if (!p.startsWith('/')) p = '/' + p;
  if (!p.endsWith('/')) p += '/';
  return p + hash;
}

export interface DocArticle {
  title: string;
  html: string;
}

/**
 * Maps the current app route to the most relevant docs page — matched against
 * the full pathname, first match wins, so per-feature sub-tab rules are listed
 * before their service overview. Route slugs mirror `serviceRoute()` in
 * pages/lib/service-categories.ts; doc slugs mirror the @docs tree. Falls back to
 * the service overview, then the docs intro.
 */
const ROUTE_TO_DOC: [test: RegExp, path: string][] = [
  // CDN (Multi-CDN console: /services/cdn/:id/*)
  [/\/cdn\/[^/]+\/analytics/, '/docs/cdn/multi-cdn/analytics'],
  [/\/cdn\/[^/]+\/domains\/[^/]+/, '/docs/cdn/multi-cdn/domain-config'],
  [/\/cdn\/[^/]+\/domains/, '/docs/cdn/multi-cdn/domains'],
  [/\/cdn\/[^/]+\/(usage|business)/, '/docs/cdn/multi-cdn/business-usage'],
  [/\/cdn\/[^/]+\/settings/, '/docs/cdn/multi-cdn/settings'],
  [/\/(cdn|multi-cdn)/, '/docs/cdn/multi-cdn/overview'],

  // WAAP (security console: /services/waap/:id/*)
  [/\/waap\/.*onboarding/, '/docs/security/waap/onboarding'],
  [/\/waap\/.*aiwaf/, '/docs/security/waap/ai-waf'],
  [/\/waap\/.*bot-/, '/docs/security/waap/bot-management'],
  [/\/waap\/.*api-protection/, '/docs/security/waap/api-protection'],
  [/\/waap\/.*analytics/, '/docs/security/waap/analytics'],
  [/\/waap\/.*emergency/, '/docs/security/waap/emergency-mitigation'],
  [/\/waap\/.*(plans|package|subscription)/, '/docs/security/waap/plans-settings'],
  [/\/waap\/.*(configuration|origin|cache-control|cors|access-control|logs)/, '/docs/security/waap/configuration'],
  [/\/waap/, '/docs/security/waap/overview'],

  // Compute (OpenNebula console: /services/compute/:id/*)
  [/\/serverless|\/functions/, '/docs/compute/serverless-functions'],
  [/\/enterprise-compute/, '/docs/compute/enterprise-compute'],
  [/\/compute\/.*instances/, '/docs/compute/cloud-compute/instances'],
  [/\/compute\/.*(network|vpc|vpn|peering|security-group|load-balancer|router|traffic)/, '/docs/compute/cloud-compute/networking'],
  [/\/compute\/.*(volume|snapshot|backup|storage)/, '/docs/compute/cloud-compute/storage'],
  [/\/compute\/.*(monitoring|usage|quota|ssh-key|access|settings|resize)/, '/docs/compute/cloud-compute/access-and-usage'],
  [/\/compute/, '/docs/compute/cloud-compute/overview'],

  // Kubernetes (/services/kubernetes/:id/* + container registry)
  [/\/(kubernetes|k8s)\/.*registry|\/registry/, '/docs/kubernetes/container-registry'],
  [/\/(kubernetes|k8s)\/.*(pools|nodes)/, '/docs/kubernetes/managed-kubernetes/node-pools'],
  [/\/(kubernetes|k8s)\/.*ingress/, '/docs/kubernetes/managed-kubernetes/ingress'],
  [/\/(kubernetes|k8s)\/.*networks/, '/docs/kubernetes/managed-kubernetes/networks'],
  [/\/(kubernetes|k8s)\/.*settings/, '/docs/kubernetes/managed-kubernetes/settings'],
  [/\/(kubernetes|k8s)\/.*clusters\/[^/]+/, '/docs/kubernetes/managed-kubernetes/cluster-detail'],
  [/\/(kubernetes|k8s)\/.*clusters/, '/docs/kubernetes/managed-kubernetes/clusters'],
  [/\/(kubernetes|k8s)/, '/docs/kubernetes/managed-kubernetes/overview'],

  // Object Storage (/services/oss/:id/*)
  [/\/oss\/.*objects/, '/docs/storage/object-storage/objects'],
  [/\/oss\/.*buckets/, '/docs/storage/object-storage/buckets'],
  [/\/oss\/.*access-keys/, '/docs/storage/object-storage/access-keys'],
  [/\/oss\/.*(iam|policies)/, '/docs/storage/object-storage/iam'],
  [/\/(oss|object-storage|storage)/, '/docs/storage/object-storage/overview'],

  // Healthcheck (/services/healthcheck/*)
  [/\/healthcheck\/.*(monitors|kuma)/, '/docs/observability/healthcheck/monitors'],
  [/\/healthcheck\/.*incidents/, '/docs/observability/healthcheck/incidents'],
  [/\/healthcheck\/.*alarms/, '/docs/observability/healthcheck/alarms'],
  [/\/healthcheck\/.*notifications/, '/docs/observability/healthcheck/notifications'],
  [/\/healthcheck\/.*playbooks/, '/docs/observability/healthcheck/playbooks'],
  [/\/(healthcheck|observability)/, '/docs/observability/healthcheck/overview'],

  // Streaming (per-service consoles from serviceRoute())
  [/\/fastchannel/, '/docs/streaming/fastchannel'],
  [/\/(aes|drm|verimatrix)/, '/docs/streaming/aes-gateway'],
  [/\/(vod|cloud-encoding)/, '/docs/streaming/cloud-encoding'],
  [/\/(livestream|live-media|lms)/, '/docs/streaming/live-media-service'],
  [/\/(transcod|on-demand)/, '/docs/streaming/on-demand-transcoding'],

  // Security (Mlytics WAF console + cert inventory)
  [/\/waf/, '/docs/security/web-application-firewall'],
  [/\/(certificates|certinvent|ssl|tiger)/, '/docs/security/ssl-tls-certificates'],

  // Databases
  [/\/database/, '/docs/databases/database-as-a-service'],

  // Account & management
  [/\/(organizations?|teams?|projects?|contacts?|admins?|people)/, '/docs/management/organizations'],
  [/\/(billing|invoice|usage-and-cost|affiliate)/, '/docs/management/usage-and-cost'],
  [/\/(account|profile|preferences)/, '/docs/management/account'],

  // Onboarding / catalog landing
  [/\/getting-started|\/onboarding/, '/docs/getting-started'],
];

export function docsPathForRoute(pathname: string): string {
  for (const [test, path] of ROUTE_TO_DOC) {
    if (test.test(pathname)) return canonicalDocPath(path);
  }
  return DOCS_HOME;
}

/** Normalizes any href/path to a site-absolute docs path ("/docs/…"), or null if external. */
export function toDocsPath(href: string): string | null {
  if (!href) return null;
  if (href.startsWith('#')) return null; // in-page anchor
  try {
    const url = new URL(href, DOCS_BASE);
    const base = new URL(DOCS_BASE);
    if (url.origin !== base.origin) return null; // external
    // Strip the Docusaurus baseUrl subpath so internal paths are canonical
    // (`/docs/…`), matching the route-derived paths in ROUTE_TO_DOC.
    let p = url.pathname;
    if (DOCS_BASE_PATH && p.startsWith(DOCS_BASE_PATH)) {
      p = p.slice(DOCS_BASE_PATH.length) || '/';
    }
    return p + url.hash;
  } catch {
    return null;
  }
}

const REMOVE_SELECTORS = [
  'script',
  'style',
  'noscript',
  'iframe',
  'object',
  'embed',
  'nav.pagination-nav',
  '.theme-doc-toc-mobile',
  '.theme-doc-toc-desktop',
  '.theme-doc-breadcrumbs',
  '.hash-link',
  'button.clean-btn', // Docusaurus code "copy" buttons
];

function sanitize(root: Element): void {
  root.querySelectorAll(REMOVE_SELECTORS.join(',')).forEach((el) => el.remove());
  // Strip inline event handlers + javascript: urls from every remaining node.
  root.querySelectorAll('*').forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      if (attr.name.startsWith('on')) el.removeAttribute(attr.name);
      if (
        (attr.name === 'href' || attr.name === 'src') &&
        /^\s*javascript:/i.test(attr.value)
      ) {
        el.removeAttribute(attr.name);
      }
    }
  });
}

function rewriteLinksAndImages(root: Element): void {
  root.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href') ?? '';
    const docsPath = toDocsPath(href);
    if (docsPath) {
      // Internal doc link → intercept in the panel (keep href for a11y/hover).
      // Canonical (trailing-slash) form so the next fetch skips the 301 hop.
      const canonical = canonicalDocPath(docsPath);
      a.setAttribute('data-docs-path', canonical);
      a.setAttribute('href', DOCS_BASE + canonical);
    } else if (/^https?:/i.test(href)) {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noreferrer noopener');
    }
  });
  root.querySelectorAll('img[src]').forEach((img) => {
    const src = img.getAttribute('src') ?? '';
    // Asset URLs are site-absolute and already carry the baseUrl subpath, so
    // prefix with the bare origin (not DOCS_BASE) to avoid doubling the subpath.
    if (src.startsWith('/')) img.setAttribute('src', DOCS_ORIGIN + src);
    img.setAttribute('loading', 'lazy');
  });
}

/**
 * Fetches a docs page and returns its cleaned article HTML + title.
 * Throws on network / parse failure so the panel can show a retry state.
 */
export async function fetchDocArticle(path: string): Promise<DocArticle> {
  // Canonical URL avoids the 301 redirect hop; `no-store` avoids replaying any
  // cached pre-CORS response that would be missing the ACAO header.
  const url = DOCS_BASE + canonicalDocPath(path);
  const res = await fetch(url, { headers: { Accept: 'text/html' }, cache: 'no-store' });
  if (!res.ok) throw new Error(`Docs request failed (${res.status})`);
  const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

  const article =
    doc.querySelector('article .theme-doc-markdown') ??
    doc.querySelector('.theme-doc-markdown') ??
    doc.querySelector('article');
  if (!article) throw new Error('Could not locate documentation content');

  sanitize(article);
  rewriteLinksAndImages(article);

  const title =
    article.querySelector('h1')?.textContent?.trim() ||
    doc.querySelector('title')?.textContent?.replace(/\s*\|.*$/, '').trim() ||
    'Documentation';

  return { title, html: article.innerHTML };
}
